import React, { useMemo } from 'react';
import { FEATURES } from '../config/features';
import { useAuth } from '../contexts/AuthContext';
import { useProjects } from '../contexts/ProjectContext';
import { TaskStatus, TaskStatusLabels, TaskStatusColors, SubElement, Priority, PriorityLabels } from '../types';
import {
  getAllSubElements,
  calculateActualHours,
  calculateProjectProgress,
} from '../data/mockData';
import { formatHours, formatPercentage } from '../utils/format';
import { BarChartIcon, CheckCircleIcon, WarningIcon, ClockIcon } from '../components/icons/Icons';
import { exportReportPdf, exportReportCsv } from '../utils/reportExport';
import { AppSectionHero, AppStatCard } from '../components/common/AppSurface';
import { Avatar } from '../components/common/Avatar';
import { StatusDonut } from '../components/charts/StatusDonut';
import { StatusBars } from '../components/charts/StatusBars';
import { BurnupArea } from '../components/charts/BurnupArea';
import { useChartColors, ChartColors } from '../hooks';
import './ReportsPage.css';

// Validated, CVD-safe chart palette resolved from theme tokens (see useChartColors).
const statusChartColor = (status: TaskStatus, c: ChartColors): string => {
  switch (status) {
    case 'ASSIGNED':    return c.series[0];
    case 'STARTED':     return c.series[1];
    case 'IN_PROGRESS': return c.series[2];
    case 'IN_REVIEW':   return TaskStatusColors.IN_REVIEW;
    case 'PAUSED':      return c.series[3];
    case 'FINISHED':    return c.series[4];
    case 'ISSUES':      return c.issue;
  }
};

const priorityChartColor = (priority: Priority, c: ChartColors): string => {
  switch (priority) {
    case 'LOW':      return c.series[0];
    case 'MEDIUM':   return c.series[1];
    case 'HIGH':     return c.series[2];
    case 'CRITICAL': return c.issue;
  }
};

const zoneTier = (pct: number): 'low' | 'mid' | 'high' | 'done' =>
  pct >= 100 ? 'done' : pct >= 66 ? 'high' : pct >= 33 ? 'mid' : 'low';

export const ReportsPage: React.FC = () => {
  const { isProjectManager } = useAuth();
  const { activeProject, activeProjectMembers } = useProjects();
  const chartColors = useChartColors();

  const reportData = useMemo(() => {
    if (!activeProject) return null;
    const allSubs = getAllSubElements(activeProject);
    const totalEst = allSubs.reduce((s, t) => s + t.estimatedHours, 0);
    const totalActual = allSubs.reduce((s, t) => s + calculateActualHours(t.timeEntries), 0);
    const progress = calculateProjectProgress(activeProject);

    const byStatus: Record<TaskStatus, number> = { ASSIGNED: 0, STARTED: 0, IN_PROGRESS: 0, IN_REVIEW: 0, PAUSED: 0, FINISHED: 0, ISSUES: 0 };
    allSubs.forEach(t => { byStatus[t.status] = (byStatus[t.status] || 0) + 1; });

    const byPriority: Record<Priority, number> = { LOW: 0, MEDIUM: 0, HIGH: 0, CRITICAL: 0 };
    allSubs.forEach(t => { byPriority[t.priority] = (byPriority[t.priority] || 0) + 1; });

    // Trabajo que el cliente añadió fuera del alcance original.
    const extraWorkTasks = allSubs.filter(t => t.isExtraWork);
    const extraWork = {
      tasks: extraWorkTasks,
      count: extraWorkTasks.length,
      estimatedHours: extraWorkTasks.reduce((s, t) => s + t.estimatedHours, 0),
      actualHours: extraWorkTasks.reduce((s, t) => s + calculateActualHours(t.timeEntries), 0),
      finished: extraWorkTasks.filter(t => t.status === 'FINISHED').length,
    };

    // Completion date of a finished task = timestamp of its status->FINISHED change.
    const finishedAt = (t: SubElement): number | null => {
      if (t.status !== 'FINISHED') return null;
      const entry = [...t.statusHistory]
        .filter(h => h.field === 'status' && h.newValue === 'FINISHED')
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];
      return new Date(entry?.timestamp || t.updatedAt).getTime();
    };
    const dueMs = (t: SubElement): number | null => (t.endDate ? new Date(t.endDate + 'T23:59:59').getTime() : null);

    // --- Delivery health ---
    const now = Date.now();
    const finishedTasks = allSubs.filter(t => t.status === 'FINISHED');
    const withDue = finishedTasks.filter(t => dueMs(t) !== null);
    const onTime = withDue.filter(t => (finishedAt(t) as number) <= (dueMs(t) as number)).length;
    const onTimeRate = withDue.length > 0 ? (onTime / withDue.length) * 100 : 0;
    const overdueTasks = allSubs.filter(t => t.status !== 'FINISHED' && dueMs(t) !== null && (dueMs(t) as number) < now);
    const overdue = overdueTasks.length;
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
    const throughput7d = finishedTasks.filter(t => { const f = finishedAt(t); return f !== null && f >= sevenDaysAgo; }).length;

    // --- Burn-up (cumulative completed vs scope, real weeks) ---
    const createdMs = (t: SubElement) => new Date(`${(t.startDate || t.createdAt || activeProject.startDate).slice(0, 10)}T00:00:00`).getTime();
    const createdTimes = allSubs.map(createdMs);
    const finishedTimes = allSubs.map(finishedAt);
    const burnStart = allSubs.length ? Math.min(...createdTimes) : now;
    const weekMs = 7 * 24 * 60 * 60 * 1000;
    const weekCount = Math.min(Math.max(Math.ceil((now - burnStart) / weekMs) + 1, 2), 12);
    const step = Math.max(now - burnStart, weekMs) / (weekCount - 1);
    const burnup = Array.from({ length: weekCount }, (_, i) => {
      const cutoff = burnStart + step * i;
      return {
        label: new Date(cutoff).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        total: createdTimes.filter(t => t <= cutoff).length,
        completed: finishedTimes.filter((t): t is number => t !== null && t <= cutoff).length,
      };
    });
    const burnMax = Math.max(burnup[burnup.length - 1]?.total ?? 1, 1);

    // --- Per-zone stats (+ at-risk = overdue in zone) ---
    const zoneStats = activeProject.zones.map(z => {
      const zoneSubs = z.sections.flatMap(s => s.elements.flatMap(e => e.subElements));
      const zoneEst = zoneSubs.reduce((s, t) => s + t.estimatedHours, 0);
      const zoneActual = zoneSubs.reduce((s, t) => s + calculateActualHours(t.timeEntries), 0);
      const zoneFinished = zoneSubs.filter(t => t.status === 'FINISHED').length;
      const zoneOverdue = zoneSubs.filter(t => t.status !== 'FINISHED' && dueMs(t) !== null && (dueMs(t) as number) < now).length;
      return {
        code: z.code,
        name: z.name,
        total: zoneSubs.length,
        finished: zoneFinished,
        atRisk: zoneOverdue,
        estimatedHours: zoneEst,
        actualHours: zoneActual,
        progress: zoneSubs.length > 0 ? (zoneFinished / zoneSubs.length) * 100 : 0,
        variance: zoneActual - zoneEst,
      };
    }).sort((a, b) => b.progress - a.progress);

    // --- Per-technician stats (+ issues, overdue, active) ---
    const techStats = activeProjectMembers.map(user => {
      const tasks = allSubs.filter(t => t.assignedTo.includes(user.id));
      const finished = tasks.filter(t => t.status === 'FINISHED').length;
      const issues = tasks.filter(t => t.status === 'ISSUES').length;
      const active = tasks.filter(t => t.status === 'IN_PROGRESS' || t.status === 'STARTED').length;
      const techOverdue = tasks.filter(t => t.status !== 'FINISHED' && dueMs(t) !== null && (dueMs(t) as number) < now).length;
      const estH = tasks.reduce((s, t) => s + t.estimatedHours, 0);
      const actH = tasks.reduce((s, t) => s + calculateActualHours(t.timeEntries), 0);
      return {
        id: user.id,
        user,
        name: user.name,
        total: tasks.length,
        finished,
        issues,
        active,
        overdue: techOverdue,
        estimatedHours: estH,
        actualHours: actH,
        completion: tasks.length > 0 ? (finished / tasks.length) * 100 : 0,
        efficiency: estH > 0 ? ((estH / Math.max(actH, 0.01)) * 100) : 0,
      };
    }).sort((a, b) => b.completion - a.completion || b.total - a.total);

    return {
      allSubs, totalEst, totalActual, progress, byStatus, byPriority,
      onTimeRate, overdue, throughput7d, burnup, burnMax, zoneStats, techStats,
      extraWork,
    };
  }, [activeProject, activeProjectMembers]);

  if (!activeProject || !reportData) {
    return (
      <div className="reports-container">
        <AppSectionHero
          icon={<BarChartIcon size={28} />}
          title="Reports"
          subtitle="No active project."
        />
        <p style={{ textAlign: 'center', color: 'var(--text-3)' }}>No active project.</p>
      </div>
    );
  }

  // Export payload mirrors exactly what the on-screen report shows (including
  // whether time-tracking columns appear), so PDF/CSV never diverge from the view.
  const buildExportData = () => ({
    progress: reportData.progress,
    allSubsLength: reportData.allSubs.length,
    onTimeRate: reportData.onTimeRate,
    overdue: reportData.overdue,
    throughput7d: reportData.throughput7d,
    timeTracking: FEATURES.TIME_TRACKING,
    totalEst: reportData.totalEst,
    totalActual: reportData.totalActual,
    zoneStats: reportData.zoneStats,
    techStats: reportData.techStats,
  });

  return (
    <div className="reports-container">
      <AppSectionHero
        icon={<BarChartIcon size={28} />}
        title="Reports"
        subtitle={<>{activeProject.name} {'\u2014'} <strong>{activeProject.code}</strong></>}
        aside={<div className="page-title-actions app-surface-inline-actions">
          <button
            className="app-surface-btn report-export-btn"
            onClick={() => { void exportReportPdf(buildExportData(), activeProject.name); }}
          >
            &#8595; PDF
          </button>
          <button
            className="app-surface-btn report-export-btn"
            onClick={() => exportReportCsv(buildExportData(), activeProject.name)}
          >
            &#8595; CSV
          </button>
        </div>}
      />

      {/* Delivery health strip */}
      <div className="reports-kpis">
        <AppStatCard icon={<BarChartIcon size={20} />} label="Overall Progress" value={formatPercentage(reportData.progress)} caption={`${reportData.byStatus.FINISHED} of ${reportData.allSubs.length} finished`} tone="blue" />
        <AppStatCard icon={<CheckCircleIcon size={20} />} label="On-time Rate" value={`${Math.round(reportData.onTimeRate)}%`} caption="Finished within due date" tone="mint" />
        <AppStatCard
          icon={<WarningIcon size={20} />}
          label="Overdue"
          value={<span style={{ color: reportData.overdue > 0 ? 'var(--accent-red-dark)' : 'var(--accent-green-dark)' }}>{reportData.overdue}</span>}
          caption="Past due, not finished"
        />
        <AppStatCard icon={<ClockIcon size={20} />} label="Throughput" value={reportData.throughput7d} caption="Completed in last 7 days" />
      </div>

      {/* Completion Burn-up + Priority donut */}
      <div className="reports-charts-row">
        <div className="reports-section reports-chart-card">
          <div className="reports-chart-head">
            <h2 className="reports-section-title">Completion Burn-up</h2>
            <div className="reports-chart-legend">
              <span><i style={{ background: chartColors.muted }} />Scope</span>
              <span><i style={{ background: chartColors.green }} />Completed</span>
            </div>
          </div>
          <div className="reports-burnup-rc">
            <BurnupArea points={reportData.burnup} />
          </div>
        </div>

        <div className="reports-section reports-chart-card">
          <h2 className="reports-section-title">Priority Breakdown</h2>
          <StatusDonut
            total={reportData.allSubs.length}
            segments={(Object.keys(reportData.byPriority) as Priority[])
              .map(p => ({ key: p, label: PriorityLabels[p], value: reportData.byPriority[p], color: priorityChartColor(p, chartColors) }))
              .filter(s => s.value > 0)}
          />
        </div>
      </div>

      {/* Status Breakdown — donut (proportion) + bars (magnitude) side by side */}
      <div className="reports-section">
        <h2 className="reports-section-title">Task Status Breakdown</h2>
        <div className="reports-status-split">
          <StatusDonut
            total={reportData.allSubs.length}
            segments={(Object.keys(reportData.byStatus) as TaskStatus[])
              .map(s => ({ key: s, label: TaskStatusLabels[s], value: reportData.byStatus[s], color: statusChartColor(s, chartColors) }))
              .filter(s => s.value > 0)}
          />
          <div className="reports-status-bars">
            <span className="reports-subhead">Task count by status</span>
            <StatusBars
              total={reportData.allSubs.length}
              bars={(Object.keys(reportData.byStatus) as TaskStatus[])
                .map(s => ({ key: s, label: TaskStatusLabels[s], value: reportData.byStatus[s], color: statusChartColor(s, chartColors) }))
                .filter(s => s.value > 0)}
            />
          </div>
        </div>
      </div>

      {/* Zone Performance — bar comparison + detail table */}
      <div className="reports-section">
        <h2 className="reports-section-title">Zone Performance</h2>
        <div className="reports-zonebars">
          {reportData.zoneStats.map(z => (
            <div key={z.code} className="reports-zonebar-row">
              <div className="reports-zonebar-label">
                <span className="ws-code">{z.code}</span>
                <span className="reports-zone-name">{z.name}</span>
              </div>
              <div className="reports-zonebar-track">
                <div className={`reports-zonebar-fill tier-${zoneTier(z.progress)}`} style={{ width: `${z.progress}%` }} />
              </div>
              <span className="reports-zonebar-pct">{z.progress.toFixed(0)}%</span>
              {z.atRisk > 0 && <span className="reports-atrisk" title="Overdue tasks in this zone">{z.atRisk} at risk</span>}
            </div>
          ))}
        </div>
        <div className="reports-table-wrap">
          <table className="reports-table">
            <thead>
              <tr>
                <th>Zone</th>
                <th>Tasks</th>
                <th>Finished</th>
                <th>At risk</th>
                {FEATURES.TIME_TRACKING && <><th>Est. Hours</th><th>Actual Hours</th><th>Variance</th></>}
                <th>Progress</th>
              </tr>
            </thead>
            <tbody>
              {reportData.zoneStats.map(z => (
                <tr key={z.code}>
                  <td>
                    <span className="ws-code">{z.code}</span>
                    <span className="reports-zone-name">{z.name}</span>
                  </td>
                  <td>{z.total}</td>
                  <td>{z.finished}</td>
                  <td className={z.atRisk > 0 ? 'reports-cell-risk' : ''}>{z.atRisk}</td>
                  {FEATURES.TIME_TRACKING && <>
                    <td>{formatHours(z.estimatedHours)}</td>
                    <td>{formatHours(z.actualHours)}</td>
                    <td style={{ color: z.variance > 0 ? 'var(--accent-red)' : 'var(--accent-green)' }}>
                      {z.variance > 0 ? '+' : ''}{formatHours(z.variance)}
                    </td>
                  </>}
                  <td>
                    <div className="reports-inline-bar">
                      <div className="reports-inline-bar-fill" style={{ width: `${z.progress}%` }} />
                    </div>
                    <span className="reports-inline-pct">{z.progress.toFixed(0)}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Team Performance — ranking with avatars */}
      {isProjectManager && (
        <div className="reports-section">
          <h2 className="reports-section-title">Team Performance</h2>
          <div className="reports-team-list">
            {reportData.techStats.map((t, i) => (
              <div key={t.id} className="reports-team-row">
                <span className="reports-team-rank">{i + 1}</span>
                <Avatar user={t.user} size={32} className="reports-team-avatar" />
                <div className="reports-team-id">
                  <span className="reports-team-name">{t.name}</span>
                  <span className="reports-team-sub">{t.total} tasks assigned</span>
                </div>
                <div className="reports-team-stats">
                  <div className="reports-team-stat">
                    <strong>{t.finished}</strong>
                    <span>Done</span>
                  </div>
                  <div className="reports-team-stat">
                    <strong>{t.active}</strong>
                    <span>Active</span>
                  </div>
                  <div className={`reports-team-stat ${t.overdue > 0 ? 'risk' : ''}`}>
                    <strong>{t.overdue}</strong>
                    <span>Overdue</span>
                  </div>
                  <div className={`reports-team-stat ${t.issues > 0 ? 'issue' : ''}`}>
                    <strong>{t.issues}</strong>
                    <span>Issues</span>
                  </div>
                </div>
                <div className="reports-team-bar">
                  <div className="reports-inline-bar wide">
                    <div className={`reports-inline-bar-fill tier-${zoneTier(t.completion)}`} style={{ width: `${t.completion}%` }} />
                  </div>
                  <span className="reports-inline-pct">{t.completion.toFixed(0)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {reportData.extraWork.count > 0 && (
        <div className="reports-card extra-work-section">
          <h3 className="reports-card-title">
            Extra work requested by client ({reportData.extraWork.count})
          </h3>
          <p className="extra-work-summary">
            {reportData.extraWork.finished} of {reportData.extraWork.count} completed
            {' · '}
            {formatHours(reportData.extraWork.estimatedHours)} estimated
            {FEATURES.TIME_TRACKING && <> {'·'} {formatHours(reportData.extraWork.actualHours)} actual</>}
            {' · '}
            outside the original scope
          </p>
          <div className="extra-work-table">
            {reportData.extraWork.tasks.map(t => (
              <div key={t.id} className="extra-work-row">
                <div className="extra-work-row-main">
                  <strong>{t.code}</strong> {t.name}
                  <span
                    className="badge small"
                    style={{ backgroundColor: TaskStatusColors[t.status], color: '#fff' }}
                  >
                    {TaskStatusLabels[t.status]}
                  </span>
                </div>
                {t.extraWorkReason && <p className="extra-work-row-reason">{t.extraWorkReason}</p>}
                {t.extraWorkBy && <p className="extra-work-row-by">Authorized by {t.extraWorkBy}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
